import { useCallback, useContext, useMemo } from 'react'

import { Checkbox } from 'antd'
import { isArray } from 'lodash'

import EntitiesSelector from './EntitiesSelector'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { getFullAbbreviation, getFullName, sortBySectionAndYear } from '../../../utils/entities/sectionToYear'

import '../../../assets/section-to-years-selector.scss'

const renderYear = sectionToYear => sectionToYear.schoolYearName

const getSectionName = sectionToYear => sectionToYear.sectionAbbreviation ?? sectionToYear.sectionName

const SectionToYearsSelector = ({
  checkable = false, disabled, dropdownMatchSelectWidth, dropdownStyle, maxTagCount, maxTagPlaceholder, multiple = 'multiple', placeholder,
  sectionToYears, selectorClassName, style = {}, tagPlaceholder, values = [], onChange
}) => {
  const gc = useContext(GlobalContext)

  const localSectionToYears = useMemo(() => {
    const sty = isArray(sectionToYears) ? sectionToYears : (gc?.sectionToYears ?? [])

    return [...sty].sort(sortBySectionAndYear)
  }, [gc?.sectionToYears, sectionToYears])

  const yearIdsBySection = useMemo(() => localSectionToYears.reduce((acc, sty) => {
    const sectionName = getSectionName(sty)

    acc[sectionName] = [...(acc[sectionName] ?? []), sty.id]

    return acc
  }, {}), [localSectionToYears])

  const handleSectionToggle = useCallback((event, sectionName) => {
    event.preventDefault()
    event.stopPropagation()

    const yearIds = yearIdsBySection[sectionName] ?? []
    const areAllSelected = yearIds.every(id => values.includes(id))
    const newValues = areAllSelected
      ? values.filter(value => !yearIds.includes(value))
      : [...values, ...yearIds.filter(id => !values.includes(id))]

    onChange(newValues, null, localSectionToYears.filter(sty => newValues.includes(sty.id)))
  }, [localSectionToYears, values, yearIdsBySection, onChange])

  const groupLabels = useMemo(() => Object.entries(yearIdsBySection).reduce((acc, [sectionName, yearIds]) => {
    const selectedCount = yearIds.filter(id => values.includes(id)).length

    acc[sectionName] = (
      <div
        className='section-to-years-selector__group'
        onMouseDown={event => event.preventDefault()}
        onClick={event => handleSectionToggle(event, sectionName)}
      >
        <Checkbox
          checked={selectedCount === yearIds.length}
          indeterminate={selectedCount > 0 && selectedCount < yearIds.length}
        />
        <span className='section-to-years-selector__section'>{sectionName}</span>
      </div>
    )

    return acc
  }, {}), [yearIdsBySection, values, handleSectionToggle])

  const sectionToYearOptions = useMemo(() => localSectionToYears.map(sty => ({
    label: getFullAbbreviation(sty),
    value: sty.id,
    tooltip: getFullName(sty),
    group: getSectionName(sty),
    groupLabel: groupLabels[getSectionName(sty)],
    entity: sty
  })), [localSectionToYears, groupLabels])

  return (
    <EntitiesSelector
      checkable={checkable}
      disabled={disabled}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      dropdownStyle={dropdownStyle}
      entityOptions={sectionToYearOptions}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={maxTagPlaceholder}
      multiple={multiple}
      optionLabelProp='label'
      placeholder={placeholder}
      renderOption={renderYear}
      selectorClassName={selectorClassName}
      showSearch
      style={style}
      tagPlaceholder={tagPlaceholder}
      values={values}
      onChange={onChange}
    />
  )
}

export default SectionToYearsSelector
